import type { Notification } from '../types/notifications.types'
import { DROPDOWN_MIN_ITEMS, DROPDOWN_MAX_ITEMS } from '../constants/notifications.constants'

/**
 * Build the visible notification list for the dropdown.
 * - Always show all unread first.
 * - Ensure at least DROPDOWN_MIN_ITEMS total by filling with most recent read if needed.
 * - Cap total at DROPDOWN_MAX_ITEMS for "View all".
 * @returns { visibleItems, showViewAll }
 */
export function buildDropdownList(notifications: Notification[]): {
  visibleItems: Notification[]
  showViewAll: boolean
} {
  const unread = notifications.filter((n) => !n.read)
  const read = notifications.filter((n) => n.read)
  const fillCount = Math.max(0, DROPDOWN_MIN_ITEMS - unread.length)
  const items = [...unread, ...read.slice(0, fillCount)]
  const showViewAll = items.length > DROPDOWN_MAX_ITEMS
  const visibleItems = items.slice(0, DROPDOWN_MAX_ITEMS)
  return { visibleItems, showViewAll }
}

/**
 * Format a createdAt timestamp into a relative time string.
 */
export function formatRelativeTime(createdAt: string): string {
  const now = Date.now()
  const diff = now - new Date(createdAt).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(createdAt).toLocaleDateString()
}

/**
 * Format a future start date (ISO string) into "Starts in X" for notifications.
 */
export function formatStartsIn(startsAtIso: string): string {
  const now = Date.now()
  const start = new Date(startsAtIso).getTime()
  const diffMs = start - now
  if (diffMs <= 0) return 'Starts soon'
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 60) return mins <= 1 ? 'Starts in 1 min' : `Starts in ${mins} mins`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return hours === 1 ? 'Starts in 1 hour' : `Starts in ${hours} hours`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'Starts tomorrow'
  if (days < 7) return `Starts in ${days} days`
  return `Starts in ${days} days`
}
